import { useMemo } from 'react'

import { isJobFailure } from '@/lib/api'

import { DocumentList } from './documents/DocumentList'
import { ProblemCases } from './documents/ProblemCases'
import { ProcessingStrip } from './documents/ProcessingStrip'
import { StatusPanels } from './documents/StatusPanels'
import { TemplateAside } from './documents/TemplateAside'
import { useDocumentActions, useNoMatchDocuments } from './documents/useDocuments'
import { useCockpitContext } from './useCockpit'

/**
 * The cockpit's first tab: everything that has been uploaded against this
 * template, and everything that can be done with it.
 *
 * The artboard lays it out in two columns, `290px minmax(0, 1fr)`: the form
 * itself on the left (`TemplateAside` — its page, the editor, its fields, and
 * the phone upload link), everything uploaded against it on the right. Below
 * `lg` the two stack, the aside first, because it is what the screen is
 * *about* and because both of its panels are short.
 *
 * Top to bottom the right column answers four questions in order: how far is the
 * batch (the status panels), is anything running right now (the strip), what
 * needs a decision from me (Problemfälle), and then the list itself. That is
 * the artboard's order and also the order somebody opening this screen asks
 * them in.
 *
 * Server state comes from `useCockpitContext()` — the layout has already
 * loaded the template, the counts, the jobs and the access list, and calling
 * `useCockpit` again here would start a second copy of the polling policy at
 * its own phase. The one query this tab owns is the document list, because it
 * is paged, filtered and selected per screen; it takes its cadence from
 * `cockpit.intervals.documents`.
 */
export function DocumentsTab() {
  const cockpit = useCockpitContext()
  const actions = useDocumentActions(cockpit)

  const failedJobs = useMemo(() => cockpit.jobs.filter(isJobFailure), [cockpit.jobs])
  const noMatchTotal = cockpit.counts?.byStatus?.noMatch ?? 0
  const problems = useNoMatchDocuments(cockpit, noMatchTotal > 0)

  const problemPending =
    actions.analyse.isPending || actions.remove.isPending || actions.move.isPending

  return (
    <div className="grid items-start gap-7 lg:grid-cols-[290px_minmax(0,1fr)]">
      <TemplateAside cockpit={cockpit} />

      <div className="flex min-w-0 flex-col gap-6">
        <StatusPanels
          templateId={cockpit.templateId}
          counts={cockpit.counts}
          isLoading={cockpit.countsQuery.isLoading}
          isError={cockpit.countsQuery.isError}
          onRetry={() => void cockpit.countsQuery.refetch()}
        />

        <ProcessingStrip
          activeJobs={cockpit.activeJobs}
          counts={cockpit.counts}
          canWrite={cockpit.canWrite}
          cancelling={actions.cancelJobs.isPending}
          cancelError={actions.cancelJobs.error}
          onCancel={(jobIds) => actions.cancelJobs.mutate(jobIds)}
        />

        <ProblemCases
          templateId={cockpit.templateId}
          documents={problems.data?.docs ?? []}
          noMatchTotal={noMatchTotal}
          failedJobs={failedJobs}
          isLoading={problems.isLoading}
          canWrite={cockpit.canWrite}
          actionPending={problemPending}
          actionError={actions.analyse.error ?? actions.remove.error ?? null}
          moveError={actions.move.error}
          movePending={actions.move.isPending}
          onForcePage={(documentId, page) =>
            actions.analyse.mutate({ documentIds: [documentId], forcePage: page })
          }
          onReanalyse={(documentIds) => actions.analyse.mutate({ documentIds })}
          onDiscard={(documentId) => actions.remove.mutate([documentId])}
          onMove={(input) => actions.move.mutate(input)}
        />

        <DocumentList cockpit={cockpit} actions={actions} />
      </div>
    </div>
  )
}

export default DocumentsTab
